import React, { useMemo, useState, useCallback, useEffect } from "react";
import axios from "axios";
import Decrypt from "../../../../shared/utils/Decrypt";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Grid,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import StoreIcon from "@mui/icons-material/Store";
import DeliveryCenterAccordion from "./DeliveryCenterAccordion";
import MapDisplayForStoresInCampaign from "./MapDisplayForStoresInCampaign";
import VendorsForSelectedDeliveryCenter from "./VendorsForSelectedDeliveryCenter";
// import DeliveryCenterAccordion from "./DeliveryCenterAccordion";
// import MapDisplayForStores from "./MapDisplayForStores";
// import VendorsForSelectedDeliveryCenter from "./VendorsForSelectedDeliveryCenter";
// import UnmappedStores from "./UnmappedStores";

const MappedStoresInCampaign = ({
  savedBatchesCount,
  mappedStores = [],
  unmappedStores = [],
  selectedDCId,
  selectedDC,
  onSelectDC,
  onMoveToMapped,
  onMoveToUnmapped,
  onBulkMoveToMapped,
  selectedMarker,
  setSelectedMarker,
  geocodeProgress,
  setSelectedDCId,
  distance,
  onSaveBatches,
  isSaving,
  onAssignVendorToBatch,
}) => {
  const [search, setSearch] = useState("");

  // ── Drag state owned here so both panels share it ─────────────────────────
  // dragPayload: { store, fromDCId: string|null }
  //   fromDCId = null → dragging from unmapped panel
  //   fromDCId = dc._id → dragging from a mapped DC's stores table
  const [dragPayload, setDragPayload] = useState(null);
  const [dragOverDCId, setDragOverDCId] = useState(null); // which accordion is highlighted

  // ── Selected rows in the unmapped panel (for bulk move) ───────────────────
  const [selectedUnmapped, setSelectedUnmapped] = useState([]);

  const filteredMapped = useMemo(() => {
    if (!search) return mappedStores;
    const s = search.toLowerCase();
    return mappedStores
      .map((dc) => {
        // Get stores from vendorBatches
        const vendorBatchStores = (dc.vendorBatches || []).flatMap(
          (batch) => batch.stores || [],
        );

        // Prefer vendorBatches stores, otherwise use dc.stores
        const storesToSearch =
          vendorBatchStores.length > 0 ? vendorBatchStores : dc.stores || [];

        const filteredStores = storesToSearch.filter(
          (store) =>
            store.details?.storeName?.toLowerCase().includes(s) ||
            store.details?.city?.toLowerCase().includes(s),
        );

        return {
          ...dc,
          stores: filteredStores,
        };
      })
      .filter((dc) => dc.stores.length > 0);
  }, [mappedStores, search]);

  // ── Drag handlers exposed to both panels ──────────────────────────────────
  const handleDragStart = useCallback((e, store, fromDCId = null) => {
    setDragPayload({ store, fromDCId });
    e.dataTransfer.effectAllowed = "move";
    // Ghost label shown during drag
    e.dataTransfer.setData("text/plain", store.details?.storeName || "Store");
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragPayload(null);
    setDragOverDCId(null);
  }, []);

  // Called by each DeliveryCenterAccordion drop zone
  const handleDropOnDC = useCallback(
    (e, dcId) => {
      e.preventDefault();
      setDragOverDCId(null);
      if (!dragPayload) return;

      const { store, fromDCId } = dragPayload;

      if (fromDCId === dcId) return; // dropped on same DC — no-op

      if (fromDCId === null) {
        // Unmapped → DC
        onMoveToMapped?.(store, dcId);
      } else {
        // DC → different DC: remove from old, add to new
        onMoveToUnmapped?.(store, fromDCId); // removes from old DC
        onMoveToMapped?.(store, dcId); // adds to new DC
      }

      setDragPayload(null);
    },
    [dragPayload, onMoveToMapped, onMoveToUnmapped],
  );
  // Called by the unmapped panel drop zone (store coming back from a DC)
  const handleDropOnUnmapped = useCallback(
    (e) => {
      e.preventDefault();
      setDragOverDCId(null);
      if (!dragPayload) return;

      const { store, fromDCId } = dragPayload;
      if (fromDCId === null) return; // already unmapped — no-op

      onMoveToUnmapped?.(store, fromDCId);
      setDragPayload(null);
    },
    [dragPayload, onMoveToUnmapped],
  );

  // Bulk move: all selected unmapped stores → a DC
  const handleBulkMove = useCallback(
    (dcId) => {
      if (!selectedUnmapped.length) return;
      onBulkMoveToMapped?.(selectedUnmapped, dcId);
      setSelectedUnmapped([]);
    },
    [selectedUnmapped, onBulkMoveToMapped],
  );

  const isDragging = dragPayload !== null;

  // ── Vendor fetch — fires whenever the selected DC changes ─────────────────
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  const serverURL = process.env.REACT_APP_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchVendors = async () => {
      setVendorsLoading(true);
      try {
        const res = await axios.get(`${serverURL}/api/vendorsForMap`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { adminApproval: 1 },
        });
        if (res?.data?.success) {
          // Decrypt phone numbers exactly as StoresTable does
          const decrypted = res.data.data.map((v) => ({
            ...v,
            phoneNumber: Decrypt(v.phoneNumber),
          }));
          setVendors(decrypted);
        } else {
          setVendors([]);
        }
      } catch (err) {
        console.error("Error fetching vendors:", err);
        setVendors([]);
      } finally {
        setVendorsLoading(false);
      }
    };

    fetchVendors();
  }, []);
  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #E5E7EB" }}
    >
      {/* ── Bulk-move banner (appears when unmapped stores are selected) ─────── */}
      {selectedUnmapped.length > 0 && (
        <Box
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: "#EEF4FF",
            borderBottom: "1px solid #C7D9F5",
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" fontWeight={600} color="primary">
            {selectedUnmapped.length} store
            {selectedUnmapped.length > 1 ? "s" : ""} selected — move to:
          </Typography>
          {mappedStores.map((dc) => (
            <Button
              key={dc._id}
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleBulkMove(dc._id)}
              sx={{ whiteSpace: "nowrap" }}
            >
              {dc.cityName} DC
            </Button>
          ))}
          <Button
            size="small"
            color="inherit"
            onClick={() => setSelectedUnmapped([])}
            sx={{ ml: "auto" }}
          >
            Clear
          </Button>
        </Box>
      )}
      <Grid container sx={{ alignItems: "flex-start" }}>
        {/* ════════ LEFT — Mapped Stores (DC Accordions) ════════ */}
        <Grid
          item
          md={6}
          sm={12}
          sx={{
            borderRight: "1px solid #E5E7EB",
            // Scroll independently if content overflows
            maxHeight: 900,
            overflowY: "auto",
          }}
        >
          <Box
            px={3}
            py={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Tabs value={0}>
              <Tab
                label={`Mapped Stores (${mappedStores.reduce((a, b) => a + b.stores.length, 0)})`}
              />
            </Tabs>
            <TextField
              size="small"
              placeholder="Search stores..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box p={2}>
            {filteredMapped.length ? (
              filteredMapped.map((dc) => (
                <DeliveryCenterAccordion
                  key={dc._id}
                  deliveryCenter={dc}
                  isSelected={selectedDCId === dc._id}
                  onSelectDC={onSelectDC}
                  // Drag-drop props
                  isDragging={isDragging}
                  isDragOver={dragOverDCId === dc._id}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverDCId(dc._id);
                  }}
                  onDragLeave={() => setDragOverDCId(null)}
                  onDrop={(e) => handleDropOnDC(e, dc._id)}
                  // So mapped stores can be dragged OUT back to unmapped
                  onStoreDragStart={(e, store) =>
                    handleDragStart(e, store, dc._id)
                  }
                  onStoreDragEnd={handleDragEnd}
                  onMoveToUnmapped={(store) =>
                    onMoveToUnmapped?.(store, dc._id)
                  }
                  distance={distance}
                />
              ))
            ) : (
              <Typography align="center" py={8} color="text.secondary">
                No Stores Found
              </Typography>
            )}
          </Box>
        </Grid>

        {/* ════════ RIGHT col — Map (top) + Unmapped Stores (bottom) ════════ */}
        <Grid
          item
          md={6}
          sm={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {/* Map — takes the top portion of the right column */}
          <Box sx={{ p: 2, borderBottom: "1px solid #E5E7EB" }}>
            <MapDisplayForStoresInCampaign
              unmappedStores={unmappedStores}
              geocodeProgress={geocodeProgress}
              selectedMarker={selectedMarker}
              setSelectedMarker={setSelectedMarker}
              selectedDCId={selectedDCId}
              setSelectedDCId={setSelectedDCId}
              onMoveToMapped={onMoveToMapped}
              selectedDC={selectedDC}
              distance={distance}
              mappedStores={mappedStores}
              onBulkMoveToMapped={onBulkMoveToMapped}
            />
          </Box>
        </Grid>

        {/* Unmapped Stores — sits directly below the map in the same column */}
        <Grid container>
          <Grid item xs={12} sx={{ p: 2, borderBottom: "1px solid #E5E7EB" }}>
            <VendorsForSelectedDeliveryCenter
              vendors={vendors}
              savedBatchesCount={savedBatchesCount}
              loading={vendorsLoading}
              selectedDC={selectedDC}
              mappedStores={mappedStores}
              distance={distance}
              onSaveBatches={onSaveBatches}
              isSaving={isSaving}
              onAssignVendorToBatch={onAssignVendorToBatch}
              ///// ------- for unmapped stores panel to know which DC is being hovered over
              unmappedStores={unmappedStores}
              selectedUnmapped={selectedUnmapped}
              setSelectedUnmapped={setSelectedUnmapped}
              isDragging={isDragging}
              dragPayload={dragPayload}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              handleDropOnUnmapped={handleDropOnUnmapped}
              setDragOverDCId={setDragOverDCId}
            />
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};
export default MappedStoresInCampaign;
